import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from '../components/ProtectedRoute';
import Loader from '../components/Loader';
import AuthLayout from '../layouts/AuthLayout';
import DashboardLayout from '../layouts/DashboardLayout';

// Lazy loaded pages for code splitting
const IntroPage = lazy(() => import('../features/auth/IntroPage'));
const LoginPage = lazy(() => import('../features/auth/LoginPage'));
const RegisterPage = lazy(() => import('../features/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('../features/auth/ForgotPasswordPage'));
const DashboardPage = lazy(() => import('../features/dashboard/DashboardPage'));
const PostsPage = lazy(() => import('../features/posts/PostsPage'));
const CreatePostPage = lazy(() => import('../features/scheduler/CreatePostPage'));
const AccountsPage = lazy(() => import('../pages/AccountsPage'));
const CalendarPage = lazy(() => import('../features/calendar/CalendarPage'));
const SmartPlannerPage = lazy(() => import('../features/planner/SmartPlannerPage'));
const ProfilePage = lazy(() => import('../features/profile/ProfilePage'));
const SocialLinksSetupPage = lazy(() => import('../features/onboarding/SocialLinksSetupPage'));
const OAuthCallbackPage = lazy(() => import('../pages/OAuthCallbackPage'));
const FailuresPage = lazy(() => import('../pages/FailuresPage'));
const TemplatesPage = lazy(() => import('../pages/TemplatesPage'));
const CampaignsPage = lazy(() => import('../pages/CampaignsPage'));
const AIAssistantPage = lazy(() => import('../pages/AIAssistantPage'));
const SocialInboxPage = lazy(() => import('../pages/SocialInboxPage'));
const CompetitorsPage = lazy(() => import('../pages/CompetitorsPage'));
const CompetitorDetailPage = lazy(() => import('../pages/CompetitorDetailPage'));
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));
const ServerErrorPage = lazy(() => import('../pages/ServerErrorPage'));
const NetworkErrorPage = lazy(() => import('../pages/NetworkErrorPage'));

const PageFallback = () => (
  <div className="flex h-[60vh] items-center justify-center">
    <Loader size="lg" />
  </div>
);

export default function AppRoutes() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        {/* Fullscreen intro (once per session) before login */}
        <Route path="/intro" element={<IntroPage />} />

        {/* Public auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        </Route>

        {/* Onboarding — protected but outside the dashboard chrome so it
            feels like a distinct step in the signup flow. */}
        <Route
          path="/onboarding/social-links"
          element={
            <ProtectedRoute>
              <SocialLinksSetupPage />
            </ProtectedRoute>
          }
        />

        {/* OAuth callback — protected so only signed-in users land here.
            The backend sends the user back after provider authorization. */}
        <Route
          path="/oauth/callback"
          element={
            <ProtectedRoute>
              <OAuthCallbackPage />
            </ProtectedRoute>
          }
        />

        {/* Protected dashboard routes */}
        <Route
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/posts" element={<PostsPage />} />
          <Route path="/create" element={<CreatePostPage />} />
          <Route path="/accounts" element={<AccountsPage />} />
          <Route path="/failures" element={<FailuresPage />} />
          <Route path="/templates" element={<TemplatesPage />} />
          <Route path="/campaigns" element={<CampaignsPage />} />
          <Route path="/ai" element={<AIAssistantPage />} />
          <Route path="/inbox" element={<SocialInboxPage />} />
          <Route path="/competitors" element={<CompetitorsPage />} />
          <Route path="/competitors/:id" element={<CompetitorDetailPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/planner" element={<SmartPlannerPage />} />
          <Route path="/profile" element={<ProfilePage />} />
        </Route>

        {/* Error pages */}
        <Route path="/500" element={<ServerErrorPage />} />
        <Route path="/network-error" element={<NetworkErrorPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
